import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Instagram,
  Facebook,
  Linkedin,
  Video,
  MoreVertical,
  RefreshCw,
  Trash2,
  ExternalLink,
  Plus,
  Share2,
  AlertCircle,
  CheckCircle2,
  Clock
} from 'lucide-react';
import { SocialAccount, SocialPlatform, PLATFORM_INFO } from '@/types/social';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

interface ConnectedAccountsProps {
  businessId: string | null;
  accounts: SocialAccount[];
  isLoading: boolean;
  onConnectNew: () => void;
  onRefresh: () => void;
}

export const ConnectedAccounts: React.FC<ConnectedAccountsProps> = ({
  businessId,
  accounts,
  isLoading,
  onConnectNew,
  onRefresh
}) => {
  const [disconnectingId, setDisconnectingId] = React.useState<string | null>(null);
  const [accountToDisconnect, setAccountToDisconnect] = React.useState<SocialAccount | null>(null);

  const handleDisconnect = async () => {
    if (!accountToDisconnect) return;

    setDisconnectingId(accountToDisconnect.id);
    try {
      const { error } = await supabase
        .from('social_accounts')
        .delete()
        .eq('id', accountToDisconnect.id);

      if (error) throw error;

      toast.success('Account disconnected', {
        description: `${accountToDisconnect.account_name || accountToDisconnect.account_username} has been disconnected.`
      });
      onRefresh();
    } catch (error) {
      console.error('Error disconnecting account:', error);
      toast.error('Failed to disconnect', {
        description: 'Please try again later.'
      });
    } finally {
      setDisconnectingId(null);
      setAccountToDisconnect(null);
    }
  };

  const handleRefreshToken = async (account: SocialAccount) => {
    // TODO: Implement token refresh via Edge Function
    toast.success('Token refreshed', {
      description: 'Account tokens have been refreshed.'
    });
  };

  const getPlatformIcon = (platform: SocialPlatform) => {
    switch (platform) {
      case 'instagram':
        return Instagram;
      case 'facebook':
        return Facebook;
      case 'linkedin':
        return Linkedin;
      case 'tiktok':
        return Video;
      default:
        return Share2;
    }
  };

  const getStatusBadge = (status: SocialAccount['status']) => {
    switch (status) {
      case 'active':
        return (
          <Badge variant="default" className="bg-green-100 text-green-800 hover:bg-green-100">
            <CheckCircle2 className="h-3 w-3 mr-1" />
            Active
          </Badge>
        );
      case 'pending':
        return (
          <Badge>
            <Clock className="h-3 w-3 mr-1" />
            Pending
          </Badge>
        );
      case 'expired':
        return (
          <Badge variant="destructive">
            <AlertCircle className="h-3 w-3 mr-1" />
            Expired
          </Badge>
        );
      case 'disconnected':
        return (
          <Badge variant="outline" className="text-muted-foreground">
            Disconnected
          </Badge>
        );
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[1, 2, 3].map((i) => (
          <Card key={i} className="animate-pulse">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-full bg-muted" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-24 bg-muted rounded" />
                  <div className="h-3 w-32 bg-muted rounded" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (accounts.length === 0) {
    return (
      <Card className="border-dashed">
        <CardContent className="pt-6">
          <div className="flex flex-col items-center text-center py-8">
            <div className="rounded-full bg-primary/10 p-4 mb-4">
              <Share2 className="h-8 w-8 text-primary" />
            </div>
            <h3 className="text-lg font-semibold mb-2">No accounts connected</h3>
            <p className="text-muted-foreground max-w-sm mb-6">
              Connect your social media accounts to start scheduling posts and managing your inbox.
            </p>
            <Button onClick={onConnectNew}>
              <Plus className="h-4 w-4 mr-2" />
              Connect Account
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {accounts.map((account) => {
          const PlatformIcon = getPlatformIcon(account.platform);
          const platformInfo = PLATFORM_INFO[account.platform];

          return (
            <Card key={account.id} className="relative">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <Avatar className="h-12 w-12">
                        <AvatarImage src={account.account_avatar_url || undefined} />
                        <AvatarFallback style={{ backgroundColor: platformInfo.bgColor }}>
                          <PlatformIcon className="h-6 w-6" style={{ color: platformInfo.color }} />
                        </AvatarFallback>
                      </Avatar>
                      <div
                        className="absolute -bottom-1 -right-1 rounded-full p-1"
                        style={{ backgroundColor: platformInfo.color }}
                      >
                        <PlatformIcon className="h-3 w-3 text-white" />
                      </div>
                    </div>
                    <div>
                      <p className="font-medium">
                        {account.account_name || account.account_username || 'Unknown Account'}
                      </p>
                      {account.account_username && (
                        <p className="text-sm text-muted-foreground">
                          @{account.account_username}
                        </p>
                      )}
                    </div>
                  </div>

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleRefreshToken(account)}>
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Refresh Token
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <a
                          href={`https://${account.platform}.com`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <ExternalLink className="h-4 w-4 mr-2" />
                          Open {platformInfo.name}
                        </a>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="text-red-600 focus:text-red-600"
                        onClick={() => setAccountToDisconnect(account)}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Disconnect
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                <div className="mt-4 flex items-center justify-between">
                  {getStatusBadge(account.status)}
                  {account.connected_at && (
                    <span className="text-xs text-muted-foreground">
                      Connected {formatDistanceToNow(new Date(account.connected_at), { addSuffix: true })}
                    </span>
                  )}
                </div>

                {account.status === 'expired' && (
                  <div className="mt-3 p-2 bg-red-50 rounded-md">
                    <p className="text-xs text-red-600">
                      Token expired. Please reconnect this account.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}

        {/* Add new account card */}
        <Card
          className="border-dashed cursor-pointer hover:border-primary/50 transition-colors"
          onClick={onConnectNew}
        >
          <CardContent className="pt-6">
            <div className="flex flex-col items-center justify-center h-full min-h-[120px] text-center">
              <div className="rounded-full bg-muted p-3 mb-3">
                <Plus className="h-6 w-6 text-muted-foreground" />
              </div>
              <p className="text-sm font-medium">Connect Account</p>
              <p className="text-xs text-muted-foreground">
                Add another platform
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Disconnect confirmation dialog */}
      <AlertDialog
        open={!!accountToDisconnect}
        onOpenChange={() => setAccountToDisconnect(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect Account</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to disconnect{' '}
              <strong>
                {accountToDisconnect?.account_name || accountToDisconnect?.account_username}
              </strong>
              ? You'll need to reconnect if you want to use it again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDisconnect}
              className="bg-red-600 hover:bg-red-700"
            >
              Disconnect
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default ConnectedAccounts;
